// =============================================
// serpResultsSaver.ts — pure DB writer dla wynikow Reverse SERP Validation
// =============================================
// Ekstrakt z NodesTab.handleSaveSerpResults (STOP-C planu auto-generate-maps).
//
// CEL: dac bulk runnerowi sciezke zapisu wynikow walidacji do `thematic_nodes`
// bez React state, bez toast. UI handler w NodesTab pozostaje cienki wrapper.
//
// PARITY: schema kolumn (search_volume, cpc, keyword_difficulty, demand_tier,
// demand_validated_at, demand_source JSONB) i mapping pol z ValidatedNodePreview
// jest DOKLADNIE identyczna z istniejacym handlerem.
// =============================================

import { supabase } from "@/integrations/supabase/client";
import type { ValidatedNodePreview } from "@/services/validation/reverseSerpValidator";
import type { ThematicNode } from "@/types/thematicMaps";
import { logDebug, logError } from "@/services/contentEngine/contentLogger";

export interface SaveSerpResultsOptions {
  /** Wezly z DB w pamieci — uzywane do match przez canonical_question_hash. */
  nodes: Pick<ThematicNode, "id" | "title" | "canonical_question_hash">[];
}

export interface SaveSerpResultsOutcome {
  /** Liczba node'ow ktore zostaly pomyslnie zaktualizowane w DB. */
  updated: number;
  /** Liczba node'ow z bledem UPDATE (DB error lub RLS block). */
  failed: number;
  /** Liczba wynikow ktore nie mialy odpowiadajacego node w `nodes` (skipped). */
  skipped: number;
  /** Pierwsza tresc bledu (do bubble do UI / log). */
  firstError?: string;
  /** Lista IDs nodes ktore mialy bledy (do retry). */
  failedNodeIds: string[];
}

/**
 * Zapisuje wyniki Reverse SERP Validation do tabeli `thematic_nodes`.
 *
 * Mapping ValidatedNodePreview -> kolumny:
 *  - search_volume / cpc / keyword_difficulty / demand_tier — flat
 *  - demand_validated_at = NOW()
 *  - demand_source JSONB = pelen snapshot decyzji + sygnaly + top3 + market_keywords[]
 *
 * Match wynikow do node'ow przez `canonical_question_hash` (stabilny przez
 * pipeline normalizacji).
 */
export async function saveSerpResultsToDb(
  mapId: string,
  results: ValidatedNodePreview[],
  options: SaveSerpResultsOptions,
): Promise<SaveSerpResultsOutcome> {
  const outcome: SaveSerpResultsOutcome = {
    updated: 0,
    failed: 0,
    skipped: 0,
    failedNodeIds: [],
  };

  if (results.length === 0) {
    logDebug(
      "SERP_SAVE",
      `Mapa ${mapId} — saveSerpResultsToDb: brak wynikow, skip`,
    );
    return outcome;
  }

  for (const v of results) {
    const nodeInDb = options.nodes.find(
      (n) => n.canonical_question_hash === v.canonical_question_hash,
    );
    if (!nodeInDb) {
      outcome.skipped++;
      continue;
    }

    try {
      const { data: updatedRow, error: updateErr } = await (supabase as any)
        .from("thematic_nodes")
        .update({
          search_volume: v.candidate_sv || 0,
          cpc: v.cpc || 0,
          keyword_difficulty: v.keyword_difficulty || 0,
          demand_tier: v.demand_tier || "unknown",
          demand_validated_at: new Date().toISOString(),
          demand_source: {
            source: "reverse_serp",
            serp_validated: true,
            decision: v.serp_validation,
            reason: v.serp_validation_reason,
            confidence: v.validation_confidence,
            overlap_score: v.overlap_score,
            original_question: v.original_question,
            validated_question: v.validated_question || null,
            suggested_question: v.suggested_question || null,
            suggested_reason: v.suggested_reason || null,
            candidate_sv: v.candidate_sv,
            market_keyword_sv: v.market_keyword_sv,
            validated_sv: v.validated_sv,
            display_sv:
              v.display_sv ||
              (v.candidate_sv > 0 ? v.candidate_sv : v.market_keyword_sv),
            best_market_keyword:
              v.best_market_keyword || v.market_keywords[0]?.keyword || null,
            entity_match_score: v.entity_match_score,
            role_match_score: v.role_match_score,
            scope_similarity_score: v.scope_similarity_score,
            rewrite_block_reason: v.rewrite_block_reason || null,
            final_decision_source: v.final_decision_source,
            market_keywords: v.market_keywords.slice(0, 10),
            top3_competitors: v.serp_top3.map((c) => ({
              domain: c.domain,
              url: c.url,
              title: c.title,
            })),
          },
        })
        .eq("id", nodeInDb.id)
        .select("id, search_volume")
        .maybeSingle();

      if (updateErr) {
        outcome.failed++;
        outcome.failedNodeIds.push(nodeInDb.id);
        if (!outcome.firstError) outcome.firstError = updateErr.message;
        logError(
          "SERP_SAVE",
          `Mapa ${mapId} — UPDATE FAIL "${nodeInDb.title}": ${updateErr.message}`,
        );
      } else if (!updatedRow) {
        outcome.failed++;
        outcome.failedNodeIds.push(nodeInDb.id);
        if (!outcome.firstError)
          outcome.firstError = "RLS block — brak uprawnien do UPDATE";
        logError(
          "SERP_SAVE",
          `Mapa ${mapId} — UPDATE EMPTY "${nodeInDb.title}": no rows returned (RLS?)`,
        );
      } else {
        outcome.updated++;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "unknown";
      outcome.failed++;
      outcome.failedNodeIds.push(nodeInDb.id);
      if (!outcome.firstError) outcome.firstError = msg;
      logError(
        "SERP_SAVE",
        `Mapa ${mapId} — UPDATE EXCEPTION "${nodeInDb.title}": ${msg}`,
      );
    }
  }

  logDebug(
    "SERP_SAVE",
    `Mapa ${mapId} — DONE: ${outcome.updated} OK, ${outcome.failed} errors, ${outcome.skipped} skipped (no match)`,
  );

  return outcome;
}
